import { AfterContentInit, Component, ContentChild, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LocalFormData, reportDesignerDto, SingleResponseModel } from 'src/app/tabms/models/base/base';
import { BaseService } from 'src/app/tabms/services/base/base.service';
import { FieldsConfigService } from 'src/app/tabms/services/global/fields-config.service';
import { EDITSUCCESS, OPERERROR, SAVESUCCESS } from 'src/app/tabms/tab-consts';
import { BaseUploadTemplateComponent } from '../base-upload-template/base-upload-template.component';
import { MatDialog } from '@angular/material/dialog';
import { FieldDictionaryListService } from 'src/app/tabms/services/global/field-dictionary-list.service';
import { PermissionsListService } from 'src/app/tabms/services/global/permissions-list.service';
import { Subject, take, takeUntil } from 'rxjs';
import { SessionWrapperService } from 'src/app/tabms/services/session/session-wrapper.service';
import { ImageHelperService } from 'src/app/tabms/services/global/image-helper.service';
import { DynamicSetFormValidatorsService } from 'src/app/tabms/services/global/dynamic-set-form-validators.service';
import { validate } from 'src/app/tabms/shared/utils/form-validation.utils';

/**
 * Supported view modes for the base-entry component
 */
export type ViewMode = 'grid' | 'cards' | 'map';

/**
 * Configuration for a single view mode
 */
export interface ViewModeConfig {
  mode: ViewMode;
  icon: string;
  labelKey: string;
}

/**
 * Default view mode configurations
 */
export const DEFAULT_VIEW_CONFIGS: ViewModeConfig[] = [
  { mode: 'grid', icon: 'view_list', labelKey: 'gridView' },
  { mode: 'cards', icon: 'apps', labelKey: 'cardsView' },
  { mode: 'map', icon: 'place', labelKey: 'mapView' }
];

@Component({
  selector: 'app-base-entry',
  templateUrl: './base-entry.component.html',
  styleUrl: './base-entry.component.scss'
})
export class BaseEntryComponent<T> implements OnInit, AfterContentInit, OnDestroy {
  //#region Inputs - Service & Permissions
  @Input() set service(_service) {
    if (!_service) return;
    this.serviceInstance = _service;
  }

  @Input() canEdit: boolean = true;
  @Input() canDelete: boolean = true;
  @Input() canCreate: boolean = true;

  @Input() disableActionsFieldName?: string;
  @Input() disableActionsValue?: any;
  @Input() disableDeleteFieldName?: string;
  @Input() disableDeleteValue?: any;
  //#endregion

  //#region Inputs - View Configuration (optional multi-view support)
  /**
   * Array of view modes to enable. Default: ['grid'] (single view, no toggle shown)
   * Example for multi-view: ['grid', 'cards', 'map']
   */
  @Input() views: ViewMode[] = ['grid'];

  /**
   * The initial/default view mode
   */
  @Input() defaultView: ViewMode = 'grid';

  /**
   * Custom view configurations (override icons, labels)
   */
  @Input() viewConfigs: ViewModeConfig[] = DEFAULT_VIEW_CONFIGS;

  /**
   * Storage key for persisting view preference in localStorage
   */
  @Input() viewStorageKey: string | null = null;
  //#endregion

  //#region Content Children
  @ContentChild('formComponent') formComponent;
  @ContentChild('cardTemplate') cardTemplate: TemplateRef<any>;
  @ContentChild('mapTemplate') mapTemplate: TemplateRef<any>;
  //#endregion

  //#region Outputs
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() dataLoaded = new EventEmitter<T[]>();
  //#endregion

  //#region Public Properties
  showCreate = false;
  serviceInstance;
  editId;
  headerClass = 'headerClass';
  title;
  form: FormGroup;
  screen: string;
  showReport: boolean;
  designReport: boolean;
  reportName: string;
  reportId: string;
  submited = false;
  permissions;
  systemMenuId: string;
  initialValues;
  ImageId: string;
  imagekey: string;

  // View mode properties
  currentView: ViewMode = 'grid';
  gridData: T[] = [];
  totalCount = 0;
  viewLoading = false;
  //#endregion

  //#region Private Properties
  private destroy$: Subject<void> = new Subject<void>();
  private keepOpenOnNextSave = false;
  private SaveAndDesignReport = false;
  private criteriaParams: any = {};
  //#endregion

  //#region constructor
  constructor(
    private baseService: BaseService,
    public fieldsConfigService: FieldsConfigService<T>,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private fieldDictionaryListService: FieldDictionaryListService,
    private permissionListService: PermissionsListService,
    private sessionService: SessionWrapperService,
    private imageHelper: ImageHelperService,
    private dynamicSetFormValidation: DynamicSetFormValidatorsService
  ) {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.systemMenuId = params['id'];
      this.fieldsConfigService.setMenuId(this.systemMenuId);
      this.fieldDictionaryListService.getPageFields(this.systemMenuId);
      this.permissionListService.getPage(this.systemMenuId);
    });

    this.fieldsConfigService.getData();
    this.screen = activatedRoute.snapshot.url[0].path;
  }
  //#endregion

  //#region lifecycle hooks
  ngOnInit() {
    // Initialize view from storage or default
    this.initializeView();

    this.permissionListService
      .getPagePermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res) {
            this.permissions = res;
          }
        }
      });
  }

  ngAfterContentInit() {
    this.baseService.formInitialized.pipe(take(1)).subscribe((res) => {
      this.form = this.formComponent.form;
      this.initialValues = this.form.getRawValue();
      this.initializationCode();
    });

    // Subscribe to criteria changes for non-grid views
    this.baseService.applyCriteria.pipe(takeUntil(this.destroy$)).subscribe((value: Object) => {
      this.criteriaParams = value;
      if (this.currentView !== 'grid') {
        this.loadDataForView();
      }
    });
  }
  //#endregion

  //#region initialization
  initializationCode() {
    // Show or hide create form and receive id in case of edit
    this.baseService.createFormDisplay.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res) return;
      this.showCreate = res?.show;
      this.editId = res?.id;

      this.dynamicSetFormValidation.setValidators(this.form, this.systemMenuId, this.destroy$, !this.editId);
      if (this.editId) {
        this.title = this.translate.instant('edit');
        this.getById(this.editId);
      }
      this.baseService.sendEditId.next(res?.id);
    });

    // Receive save action
    this.baseService.saveForm.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.onSubmit();
    });

    // Receive clear action
    this.baseService.clearForm.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.form.reset();
      this.form.patchValue(this.initialValues);

      if (this.formComponent && typeof (this.formComponent as any).resetImageState === 'function') {
        (this.formComponent as any).resetImageState();
      }
      if (this.formComponent && typeof (this.formComponent as any).resetFileState === 'function') {
        (this.formComponent as any).resetFileState();
      }
      if (this.formComponent && typeof (this.formComponent as any).tabPermissionTypeCode) {
        (this.formComponent as any).tabPermissionTypeCode = null;
      }
    });

    // Receive temporary save or restore action
    this.baseService.saveRetrieveLocalData.pipe(takeUntil(this.destroy$)).subscribe((saveorRetrieve: number) => {
      if (saveorRetrieve == 1) {
        this.saveLocalData();
      } else if (saveorRetrieve == 2) {
        this.retrieveLocalData();
      }
    });

    this.baseService.uploadfromExcel.pipe(takeUntil(this.destroy$)).subscribe((printListFields) => {
      if (printListFields) {
        let extraDownloadProperties = this.formComponent.beforeDownloadTemplateWork(); //['password', 'empCode'];
        this.dialog.open(BaseUploadTemplateComponent, {
          width: '800px',
          direction: this.baseService.getDirection(),
          data: {
            service: this.serviceInstance,
            displayListFields: printListFields,
            extraDownloadProperties: extraDownloadProperties,
            type: 1
          } // 1 for one row and 0 for list
        });
      }
    });
    // recieve upload from excel action
    this.baseService.uploadedData.pipe(takeUntil(this.destroy$)).subscribe((value: object) => {
      this.setUploadedData(value);
    });

    // Receive show report action
    this.baseService.showReport.pipe(takeUntil(this.destroy$)).subscribe({
      next: (report: reportDesignerDto) => {
        this.showReport = true;
        this.reportName = report.reportfilename;
        this.reportId = report.id;
        this.onSubmit();
      }
    });

    this.baseService.designReport.pipe(takeUntil(this.destroy$)).subscribe({
      next: (report: reportDesignerDto) => {
        this.designReport = true;
        this.reportName = report.reportfilename;
        this.reportId = report.id;
        this.onSubmit();
      }
    });

    // Listen to Save & Copy flag to control cancel behavior
    this.baseService.keepFormOpenOnSave
      .pipe(takeUntil(this.destroy$))
      .subscribe((keep) => {
        this.keepOpenOnNextSave = !!keep;
      });

    // Listen to Save & Design behavior
    this.baseService.saveAndDesgin
      .pipe(takeUntil(this.destroy$))
      .subscribe((Save) => {
        this.SaveAndDesignReport = !!Save;
        this.onSubmit();
      });

    // Refresh data for non-grid views
    this.baseService.refreshTable.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      if (value && this.currentView !== 'grid') {
        this.loadDataForView();
      }
    });
  }

  cancelForm() {
    this.form.reset();
    this.form.patchValue(this.initialValues); //reset to ititial values
    this.submited = false;
    this.baseService.createFormDisplay.next({ show: false, id: null });
  }
  //#endregion

  //#region crud
  onSubmit() {
    this.submited = true;
    if (this.formComponent.createUserCheck) {
      this.add();
    } else {
      if (validate(this.form)) {
        if (!this.editId) this.add();
        else this.update();
      }
    }
  }

  async add() {
    const asyncBeforeSaveWork = this.formComponent.beforeSaveWork_async ? await this.formComponent.beforeSaveWork_async() : true;
    const syncBeforeSaveWork = this.formComponent.beforeSaveWork ? this.formComponent.beforeSaveWork() : true;

    if (syncBeforeSaveWork && asyncBeforeSaveWork) {
      let formData: T = this.form.getRawValue();

      Object.keys(formData).forEach(key => {
        if (key.toLowerCase() === 'branchid' && formData[key] === null) {
          formData[key] = this.sessionService.DefaultBranchID;
        }
      });

      if (this.formComponent.hasImage) {
        formData = await this.imageHelper.formHasImage(formData, this.destroy$);
      }

      this.serviceInstance.create(formData).pipe(takeUntil(this.destroy$)).subscribe(
        async (res) => {
          let id = res?.result == null ? res.id : res.result.id;
          if (this.showReport) this.displayReport(id);
          if (this.designReport || this.SaveAndDesignReport) this.displayReportDesigner(id);
          this.baseService.showSuccessMessage(SAVESUCCESS);
          this.removeLocalStorage();
          if (this.formComponent.afterSaveWork) {
            this.formComponent.afterSaveWork(id);
          } else if (this.formComponent.afterSaveWork_async) {
            await this.formComponent.afterSaveWork_async(id);
          }
          if (this.keepOpenOnNextSave) {
            this.baseService.createFormDisplay.next({ show: true, id: '' });
            this.keepOpenOnNextSave = false;
          } else {
            this.cancelForm();
          }
          this.baseService.refreshTable.next(true);
          this.serviceInstance.broadcastUpdate();
        },
        (err) => {
          this.baseService.showErrorMessage(OPERERROR);
        }
      );
    }
  }

  async update() {
    const asyncBeforeUpdateWork = this.formComponent.beforeUpdateWork_async ? await this.formComponent.beforeUpdateWork_async() : true;
    const syncBeforeUpdateWork = this.formComponent.beforeUpdateWork ? this.formComponent.beforeUpdateWork() : true;

    if (syncBeforeUpdateWork && asyncBeforeUpdateWork) {
      let formData: T = { ...this.form.getRawValue(), id: this.editId };
      if (this.formComponent.hasImage) {
        formData = await this.imageHelper.formHasImage(formData, this.destroy$);
      }

      this.serviceInstance.update(this.editId, formData).pipe(takeUntil(this.destroy$)).subscribe({
        next: async (res) => {
          let id = this.editId;
          if (this.showReport) this.displayReport(id);
          if (this.designReport || this.SaveAndDesignReport) this.displayReportDesigner(id);
          this.baseService.showSuccessMessage(EDITSUCCESS);
          if (this.formComponent.afterUpdateWork) {
            this.formComponent.afterUpdateWork(id);
          } else if (this.formComponent.afterUpdateWork_async) {
            await this.formComponent.afterUpdateWork_async(id);
          }
          if (this.keepOpenOnNextSave) {
            this.baseService.createFormDisplay.next({ show: true, id: '' });
            this.keepOpenOnNextSave = false;
          } else {
            this.cancelForm();
          }
          this.baseService.refreshTable.next(true);
          this.serviceInstance.broadcastUpdate();
        },
        error: (err) => {
          this.baseService.showErrorMessage(OPERERROR);
        }
      });
    }
  }

  getById(id) {
    this.serviceInstance.get(id).pipe(takeUntil(this.destroy$)).subscribe((res: SingleResponseModel<T>) => {
      let data;
      if (res?.result) data = res.result;
      else data = res;
      this.form.patchValue(data);
      this.formComponent.afterGetDataWork ? this.formComponent.afterGetDataWork(data) : null;
    });
  }
  //#endregion

  //#region report
  displayReport(id) {
   // debugger;
    let properties = { Id: this.reportId, reportName: this.reportName, parameters: { id: id } };
    let reportsUrl = `${window.location.origin}/reports/${JSON.stringify(properties)}`;
    window.open(reportsUrl);
    this.showReport = false;
    this.SaveAndDesignReport = false;
    this.baseService.createFormDisplay.next({ show: false, id: null });
    this.baseService.refreshTable.next(true);
    this.cancelForm();
  }
  displayReportDesigner(id) {
    //debugger;
   // let properties = { Id: this.reportId, reportName: this.reportName, parameters: { id: id } };
    let properties = {Id: this.reportId ?? id };
    let reportsUrl = `${window.location.origin}/report-design/${JSON.stringify(properties)}`;
    window.open(reportsUrl);
    this.designReport = false;
    this.baseService.createFormDisplay.next({ show: false, id: null });
    this.baseService.refreshTable.next(true);
    this.cancelForm();
  }
 
  //#endregion

  //#region local data
  saveLocalData() {
    const localFormData: LocalFormData = { screen: this.screen, showrestorebtn: true, formvalue: this.form.getRawValue() };
    let localDataArray: LocalFormData[];
    localDataArray = JSON.parse(localStorage.getItem('localFormData') || '[]');
    let indexToUpdate = localDataArray.findIndex((x) => x.screen == this.screen);
    if (indexToUpdate > -1) localDataArray[indexToUpdate] = localFormData;
    else localDataArray.push(localFormData);

    localStorage.setItem('localFormData', JSON.stringify(localDataArray));
  }

  retrieveLocalData() {
    const localDataArray: LocalFormData[] = JSON.parse(localStorage.getItem('localFormData') || '[]');
    const localData = localDataArray.find((x) => x.screen == this.screen);
    if (localData) {
      this.form.patchValue(localData.formvalue);
      this.formComponent.afterGetDataWork ? this.formComponent.afterGetDataWork(localData.formvalue) : null;
    }
  }

  removeLocalStorage() {
    let localDataArray: LocalFormData[];
    localDataArray = JSON.parse(localStorage.getItem('localFormData') || '[]');
    let indexToDelete = localDataArray.findIndex((x) => x.screen == this.screen);
    if (indexToDelete > -1) localDataArray.splice(indexToDelete, 1);
    localStorage.setItem('localFormData', JSON.stringify(localDataArray));
    this.baseService.saveRetrieveLocalData.next(0);
  }
  //#endregion

  //#region import from excel
  setUploadedData(value) {
    this.form.patchValue(value);
  }
  //#endregion

  //#region View Mode Methods
  /**
   * Get the available view configurations based on enabled views
   */
  getAvailableViews(): ViewModeConfig[] {
    return this.viewConfigs.filter(config => this.views.includes(config.mode));
  }

  /**
   * Check if a specific view mode is enabled
   */
  isViewEnabled(mode: ViewMode): boolean {
    return this.views.includes(mode);
  }

  /**
   * Switch to a different view mode
   */
  setViewMode(mode: ViewMode): void {
    if (!this.isViewEnabled(mode)) return;

    this.currentView = mode;
    this.viewModeChange.emit(mode);

    // Persist preference
    if (this.viewStorageKey) {
      try {
        localStorage.setItem(this.viewStorageKey, mode);
      } catch {
        // Ignore storage errors
      }
    }

    // Load data for the new view if needed
    if (mode !== 'grid') {
      this.loadDataForView();
    }
  }

  /**
   * Initialize view mode from storage or default
   */
  private initializeView(): void {
    let savedView: ViewMode | null = null;

    if (this.viewStorageKey) {
      try {
        savedView = localStorage.getItem(this.viewStorageKey) as ViewMode;
      } catch {
        // Ignore storage errors
      }
    }

    if (savedView && this.isViewEnabled(savedView)) {
      this.currentView = savedView;
    } else if (this.isViewEnabled(this.defaultView)) {
      this.currentView = this.defaultView;
    } else {
      this.currentView = this.views[0] || 'grid';
    }
  }

  /**
   * Load all data for non-grid views (cards, map)
   */
  loadDataForView(): void {
    if (!this.serviceInstance) return;

    this.viewLoading = true;

    // Determine fielddisplayon based on current view
    // 1 = Header/Grid, 4 = Card, 5 = Map
    const fieldDisplayOn = this.currentView === 'map' ? 5 : (this.currentView === 'cards' ? 4 : 1);

    const hasParams = this.criteriaParams && Object.keys(this.criteriaParams).length > 0;
    const serviceCall = hasParams && this.serviceInstance.gridList
      ? this.serviceInstance.gridList(this.systemMenuId, null, fieldDisplayOn, 0, 100, this.criteriaParams)
      : this.serviceInstance.getAll();

    serviceCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const rawItems = res?.result?.items || res?.result || res?.items || [];

        // Process items - extract and clean _entitydto
        this.gridData = rawItems.map(item => {
          const entity = item._entitydto ?? {};
          const cleanEntity = Object.fromEntries(
            Object.entries(entity).map(([key, value]) => [
              key.replace(/\./g, '_'),
              value
            ])
          );
          return { ...item, ...cleanEntity };
        });

        this.totalCount = res?.result?.totalcount || rawItems.length;
        this.dataLoaded.emit(this.gridData);
        this.viewLoading = false;
      },
      error: () => {
        this.viewLoading = false;
      }
    });
  }

  /**
   * Open edit form for an item (called from cards or map view)
   */
  editItem(item: T): void {
    const id = (item as any).id;
    if (id) {
      this.baseService.createFormDisplay.next({ show: true, id });
    }
  }

  /**
   * Open create form (can be called from any view)
   */
  createItem(): void {
    this.baseService.createFormDisplay.next({ show: true, id: null });
  }

  /**
   * Delete an item (called from cards or map view)
   */
  deleteItem(item: T): void {
    const id = (item as any).id;
    if (!id) return;

    if (confirm(this.translate.instant('deleteConfirmation'))) {
      this.serviceInstance.delete(id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.baseService.showSuccessMessage(this.translate.instant('deleteSuccess'));
          this.baseService.refreshTable.next(true);
        },
        error: () => {
          this.baseService.showErrorMessage(this.translate.instant('deleteError'));
        }
      });
    }
  }

  /**
   * Get display name for an item in cards view
   */
  getItemDisplayName(item: any, index: number): string {
    const possibleFields = ['name', 'araname', 'latname'];

    for (const field of possibleFields) {
      if (item[field]) return item[field];
    }

    const keys = Object.keys(item);
    const aranameField = keys.find(k => k.endsWith('araname'));
    if (aranameField && item[aranameField]) return item[aranameField];

    const latnameField = keys.find(k => k.endsWith('latname'));
    if (latnameField && item[latnameField]) return item[latnameField];

    return `Item ${index + 1}`;
  }
  //#endregion

  //#region Lifecycle Cleanup
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.baseService.createFormDisplay.next(null);
    this.fieldsConfigService.setMenuId('');
  }
  //#endregion
}
